
<script setup>
import {reactive, onMounted, computed, nextTick, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";
import {QuillEditor} from "@vueup/vue-quill";


const editorInit = {
  selector: 'textarea',  // change this value according to the HTML
  menubar: false,
  branding: false,
  plugins: 'lists styles',
  toolbar: 'undo redo | styles | bold italic | bullist numlist | outdent indent',
  style_formats: [
    { title: 'Paragraph', format: 'p' },
    { title: 'Heading 6', format: 'h6' },
    { title: 'Heading 5', format: 'h5' },
    { title: 'Heading 4', format: 'h4' },
    { title: 'Heading 3', format: 'h3' },
    { title: 'Heading 2', format: 'h2' },
    { title: 'Heading 1', format: 'h1' }
  ]
}

const props = defineProps({
  contextIdA: String,
  personId: String,
  clientId: String,
  claimId: String
});

const form = ref(null);

const compName = 'NotesComponent';

// ClassicEditor.defaultConfig = {
//   toolbar: {
//     items: [
//       'heading',
//       '|',
//       'bold',
//       'italic',
//       '|',
//       'bulletedList',
//       'numberedList',
//       '|',
//       'insertTable',
//       '|',
//       'undo',
//       'redo'
//     ]
//   },
//   table: {
//     contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
//   },
//   language: 'en'
// };

const data = reactive({
  editor: window['ClassicEditor'],
  valid: true,
  showAdditionalNote: false,
  openNotesForm: false,
  openAdditionalNoteForm: false,
  dataLoading: true,
  loading: false,
  show: [],
  notes: [],
  rules: [
    (v) => !!v || 'Note is required',
    (v) => v.length <= 70 || 'Max 70 characters',
  ],
  loaded: true,
  isEditing: false,
  search: '',
  editedItem: {
    note: '',
    additionalInfo: '',
    isprivate: false
  },
  defaultItem: {
    note: '',
    additionalInfo: '',
    isprivate: false
  },
})

const filteredItems = computed(()=>{
  if(data.search === null || data.search === ''){
    return data.notes
  }else{
    return data.notes.filter((item) => {
      let note = null
      let ai = null
      let ca = null
      if(item.note !== null){
        note = item.note
      }else{
        note = ''
      }
      if(item.additionalinfo !== null){
        ai = item.additionalinfo
      }else{
        ai = ''
      }
      if(item.created_at !== null){
        ca = item.created_at
      }else{
        ca = ''
      }
      return (
          note.toLowerCase().includes(data.search.toLowerCase()) ||
          ai
              .toLowerCase()
              .includes(data.search.toLowerCase()) ||
          ca
              .toLowerCase()
              .includes(data.search.toLowerCase())
      )
    })
  }

})

const toggleActive = (index) => {
  if (data.show.includes(index)) {
    data.show = data.show.filter((entry) => entry !== index)
    return
  }
  data.show.push(index)
}

const formatTimestampByLocale = (timestamptz) => {
  const date = new Date(timestamptz)
  const timeOptions = {
    hour12: true,
    hour: "numeric",
    minute: "2-digit",
  }
  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZoneName:'short'
  }
  return date.toLocaleString('en', { ...dateOptions, ...timeOptions })
}

const listAll = () => {
  ApiWrapper.post('notes', 'list-notes', {
        clientId: props.clientId,
        personId: props.personId,
        claimId: props.claimId
      })
      .then((response) => {
        if(response.notes){
          for(let x in response.notes){
            if(typeof response.notes[x].note === 'string' && response.notes[x].note.length > 0) {
              response.notes[x].note = response.notes[x].note.replace(/\\r\\n/g, '<br>')
            }
          }
          for(let x in response.notes){
            if(typeof response.notes[x].additionalinfo === 'string' && response.notes[x].additionalinfo.length > 0) {
              response.notes[x].additionalinfo = response.notes[x].additionalinfo.replace(/\\r\\n/g, '<br>')
            }
          }
          data.notes = response.notes
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
      })
}

const editItem = (item) => {
  data.isEditing = true
  data.editedItem = JSON.parse(JSON.stringify(item))
  data.editedItem.isprivate = false;
  toggleActive()
  data.openNotesForm = true
}

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this item?'
  )
  if (deleteMessage === true) {
    data.loading = true
    ApiWrapper.post('notes', 'delete-note', {
          item: item,
          clientId: props.clientId,
          personId: props.personId,
          claimId: props.claimId
        })
        .then((response) => {
          activeComponentStore.notifyComponents(['notes'],compName);
          listAll()
          data.loading = false
          ToastUtil.successMsg('Deleted note');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const newNote = () => {
  if (form.value.validate()) {
    data.loading = true
    ApiWrapper.post('notes', 'create-note', {
          newNote: data.editedItem,
          clientId: props.clientId,
          personId: props.personId,
          claimId: props.claimId
        })
        .then((response) => {
          activeComponentStore.notifyComponents(['notes'],compName);
          listAll()
          closeAddNote()
          ToastUtil.successMsg('Created note');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const saveNote = (item, index) => {
  ApiWrapper.post('notes', 'edit-note', { editedItem: item })
      .then((response) => {
        activeComponentStore.notifyComponents(['notes'],compName);
        listAll()
        ToastUtil.successMsg('Updated note');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const wrapSrcdocPage = (srcdoc) => {
  /* eslint-disable no-useless-escape */
  return `
              <html>
                <head>
                  <link
                    href="https://fonts.googleapis.com/css?family=Roboto:100,300,400,500,700,900"
                    rel="stylesheet"
                  />
                  <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, minimal-ui"
                  />
                  <style type="text/css">
                    .v-application--wrap {
                      min-height: 0;
                    }

                    body {
                      font-family: 'Roboto', sans-serif;
                    }
                  </style>
                </head>
              <body>
                <div>
                  ${srcdoc}
                </div>
                <script src="https://cdn.jsdelivr.net/npm/iframe-resizer@4.3.2/js/iframeResizer.contentWindow.min.js"><\/script>
              </body>
              </html>
            `
}

const closeAddNote = () => {
  data.openNotesForm = false
  data.openAdditionalNoteForm = false
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem)
    data.isEditing = false
  })
}

onMounted(()=>{
  listAll()
})
</script>

<template>
  <v-container fluid v-if="data.loaded===true">
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar density="compact" flat color="white">
        <v-toolbar-title>Notes</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>

        <v-spacer></v-spacer>
        <!-- New Note Button -->
        <v-btn
            size="small" variant="elevated"
            v-if="!data.openNotesForm && Permissions.checkPermissions(['notes.create'])"
            class="primary"
            @click="data.openNotesForm = true"
        >
          New Note
        </v-btn>
      </v-toolbar>

      <!-- New Note Form -->
      <v-form
          ref="form"
          v-model="data.valid"
          lazy-validation
          v-if="data.openNotesForm"
      >
        <div>
          <v-card flat>
            <v-list-item>
              <QuillEditor theme="snow" v-model:content="data.editedItem.note" contentType="html" toolbar="essential"/>
            </v-list-item>

            <v-card-actions>
              <!-- Add Additional Info Button -->
              <v-btn
                  variant="text"
                  v-if="data.openAdditionalNoteForm"
                  class="grey lighten-2 ml-2"
                  size="x-small"
                  @click="data.openAdditionalNoteForm = false"
              >
                <v-icon left size="small">mdi-window-close</v-icon>
                Cancel Additional Info
              </v-btn>
              <v-btn
                  v-else
                  class="grey lighten-2 ml-2"
                  size="x-small"
                  @click="data.openAdditionalNoteForm = true"
              >
                <v-icon left size="x-small">mdi-plus</v-icon>
                Add Additional Info (Optional)
              </v-btn>
            </v-card-actions>

            <!-- Display Add Additional Info Text Field -->
            <v-card-text v-show="data.openAdditionalNoteForm">
              <v-textarea v-model="data.editedItem.additionalInfo"></v-textarea>
            </v-card-text>

            <v-card-actions>
              <v-btn size="x-small" variant="elevated" color="error" class="ml-2" @click="closeAddNote"
              >Cancel</v-btn
              >
              <v-btn
                  size="small"
                  variant="elevated"
                  color="primary ml-2"
                  @click="newNote"
                  :disabled="!data.valid"
              >Save</v-btn
              >
            </v-card-actions>
            <v-divider></v-divider>
          </v-card>
        </div>
      </v-form>

      <!-- <v-container> -->
      <v-row>
        <v-col cols="6" class="py-0">
          <!-- SEARCH Bar -->
          <v-text-field
              density="compact"
              class="ml-4"
              type="text"
              v-model="data.search"
              label="Search Notes"
              append-icon=" mdi-magnify"
          >
          </v-text-field>
        </v-col>
      </v-row>

      <!-- LIst of Notes -->
      <v-row density="compact">
        <v-col>
          <v-list density="compact" two-line class="py-0">
            <template v-for="(item, index) in filteredItems" :key="item.id">
              <v-divider></v-divider>
              <v-list-item density="compact" class="py-0">
                  <iframe sandbox="allow-scripts allow-same-origin" v-resize.quiet="{ log: true, checkOrigin: false }"  width="100%" frameborder="0" :srcdoc="wrapSrcdocPage(item.note)"></iframe>
                  <v-list-item-subtitle class="font__size__subtitle">
                    {{item.name}} - {{formatTimestampByLocale(item.created_at)}}
                    <span>
                              <v-btn
                                  variant="text"
                                  class="primary--text ml-2"
                                  size="x-small"
                                  @click="toggleActive(index)"
                                  v-if="item.additionalinfo != null && item.additionalinfo.length > 0"
                              >
                                <v-icon left size="x-small"
                                >{{ data.show.includes(index) ? 'mdi-chevron-up' :
                                    'mdi-chevron-down' }}</v-icon
                                >
                                More Notes
                              </v-btn>
                            </span>
                  </v-list-item-subtitle>
                  <v-icon v-if="Permissions.checkPermissions(['notes.delete'])" @click="deleteItem(item)">
                    mdi-delete
                  </v-icon>
              </v-list-item>
              <div class="pb-0 px-4" v-show="data.show.includes(index)">
                <iframe sandbox="allow-scripts allow-same-origin" v-resize.quiet="{ log: true, checkOrigin: false }"  width="100%" frameborder="0" :srcdoc="wrapSrcdocPage(item.additionalinfo)"></iframe>
              </div>
            </template>
          </v-list>
        </v-col>
      </v-row>
      <!-- </v-container> -->
    </v-card>
  </v-container>
</template>

<style scoped>
  .container {
    padding: 5px;
  }

  [v-cloak] {
    display: none;
  }

  /* .font__size__subtitle {
    font-size: 12px !important;
  } */
  .v-list-item-title {
    font-size: 13px !important;
    color: black !important;
  }
  .v-list-item-subtitle {
    font-size: 10px !important;
    color: black !important;
  }
  /* .v-label {
    font-size: 13px !important;
  } */
  .ck.ck-editor {
    width: 100% !important;
  }

  iframe {
    width: 1px;
    min-width: 100%;
  }
</style>